import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import Grid from "@mui/material/Grid";
import InputControl from '../../Components/Common/TextField/TextField';
import DropDown from '../../Components/Common/DropDown/DropDown';
import Button from '../../Components/Button/Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { GetAllAgencies, GetAllCountries, GetSolutionHubs, SaveAgency, SaveCountry, SaveEntity } from '../../Reducers/dcoi';
import { ArrowBackIos } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import AgencyDialog from '../../Components/AgencyDialog/AgencyDialog';
import CountryDialog from '../../Components/CountryDialog/CountryDialog';
import Spinner from '../../Components/Common/Spinner/Spinner';
import Toster from '../../Components/Common/Toster/Toster';

function AddNewEntity() {
  const location = useLocation();
  const EntityData = location.state?.EntityData || null;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Keep a reference to prevent unnecessary re-renders
  const agenciesRef = useRef([]);
  const countriesRef = useRef([]);

  const { SolutionHubs, agenciesList, countriesList, inProgress } = useSelector((state) => state.dcoi);

  const [popupData, setPopupData] = useState({ agencyData: "", agencyCodeData: "", shortCodeData: "" });
  const [popupCountry, setPopupCountry] = useState({ CountryName: "", CountryCode: "" });
  const [agencyName, setAgencyName] = useState(EntityData?.AgencyId || "");
  const [countryName, setCountryName] = useState(EntityData?.CountryId || "");
  const [hfm, setHfm] = useState(EntityData?.EntityName || "");
  const [entity, setEntity] = useState(EntityData?.EntityDescription || "");
  const [solutionHubName, setSolutionHubName] = useState(EntityData?.SolutionHubId || "");
  const [email, setEmail] = useState(EntityData?.SupervisorEmail || "");
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenCounty, setIsOpenCountry] = useState(false);
  const [alert, setAlert] = useState(false);
  const [message, setMessage] = useState();
  const [severity, setSavirity] = useState();
  const [emailError, setEmailError] = useState("");


  // Fetch agencies and countries only once
  useEffect(() => {
    if (!agenciesRef.current.length) {
      dispatch(GetAllAgencies()).then((res) => {
        agenciesRef.current = res.payload;
      });
    }
    if (!countriesRef.current.length) {
      dispatch(GetAllCountries()).then((res) => {
        countriesRef.current = res.payload;
      });
    }
    dispatch(GetSolutionHubs());
  }, [dispatch]);

  // Memoize solution hub short code
  const solutionHubShort = useMemo(() => {
    const selectedHub = SolutionHubs.find(hub => hub.id === solutionHubName);
    return selectedHub ? selectedHub.Code : "";
  }, [solutionHubName, SolutionHubs]);

  const changeAgency = (e) => setAgencyName(e);
  const changeCountry = (e) => setCountryName(e);
  const changeHub = (e) => setSolutionHubName(e);

  const isFormValid = () => {
    return hfm && entity && agencyName && countryName && solutionHubName;
  };

  const isValidEmail = (value) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const handleSave = () => {
    if(email && !isValidEmail(email)) {
      setEmailError("Please enter a valid email.");
      return;
    }
    else {
      setEmailError("");
    }
    if (EntityData) {
      dispatch(SaveEntity({
        Id: EntityData.ID,
        hfmCode: hfm,
        description: entity,
        agencyId: agencyName,
        countryId: countryName,
        solutionHubId: solutionHubName,
        SupervisorEmail: email
      })).then((res) => {
        if (res.payload.status === 200) {
          setHfm("");
          setEntity("");
          setAgencyName("");
          setCountryName("");
          setSolutionHubName("");
          setEmail("");
          navigate('/app-dcoi/sitecontent/ManageEntity');
        }
      })
    } else {
      dispatch(SaveEntity({
        hfmCode: hfm,
        description: entity,
        agencyId: agencyName,
        countryId: countryName,
        solutionHubId: solutionHubName,
        SupervisorEmail: email
      })).then((res) => {
        if (res?.payload?.data[0]?.status === "done" && res?.payload?.status === 200) {
          setAlert(true);
          setSavirity("success");
          setMessage(res?.payload?.data[0]?.success)
          setHfm("");
          setEntity("");
          setAgencyName("");
          setCountryName("");
          setSolutionHubName("");
          setEmail("");
          setTimeout(()=>{
            navigate('/app-dcoi/sitecontent/ManageEntity');
          },1000)
        } else if (res?.payload?.data[0]?.status === "error") {
          setAlert(true);
          setMessage(res?.payload?.data[0]?.error)
          setSavirity("error");
        } else {
          setAlert(true);
          setMessage("Something Went Wrong")
          setSavirity("error");
        }
      })
    }
  };

  const submitChange = () => {
    dispatch(SaveAgency({
      AgencyName: popupData.agencyData,
      AgencyCode: popupData.agencyCodeData,
      ShortName: popupData.shortCodeData
    })).then((res) => {
      if (res.payload.status === 200) {
        setAgencyName(res.payload.data[0].ID)
        setPopupData({ agencyData: "", agencyCodeData: "", shortCodeData: "" });
        setIsOpen(false);
        dispatch(GetAllAgencies());
      }
    });
  };

  const submitChangeCountry = () => {
    dispatch(SaveCountry({
      CountryName: popupCountry.CountryName,
      CountryCode: popupCountry.CountryCode
    })).then((res) => {
      if (res.payload.status === 200) {
        setCountryName(res.payload.data[0].ID)
        setPopupCountry({ CountryName: "", CountryCode: "" });
        setIsOpenCountry(false);
        dispatch(GetAllCountries());
      }
    });
  };

  return (
    <>
    <Toster message={message} severity={severity} alert={alert} setAlert={setAlert} />
      <AgencyDialog isOpen={isOpen} onClose={() => setIsOpen(false)} submitChange={submitChange} popupData={popupData} setPopupData={setPopupData} />
      <CountryDialog isOpen={isOpenCounty} onClose={() => setIsOpenCountry(false)} submitChangeCountry={submitChangeCountry} popupCountry={popupCountry} setPopupCountry={setPopupCountry} />

      <div className='headerNavigation'>
        <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>{EntityData ? "Edit Entity" : "Add Entity"}</Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ color: "Back to List", cursor: "pointer", "&:hover": { textDecoration: "underline" } }} onClick={() => navigate(`/app-dcoi/sitecontent/ManageEntity`)}>
            <ArrowBackIos fontSize='inherit' /> Back to List
          </Typography>
        </Box>
      </div>

      <Box className='boxAlignment'>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>HFM Code<span className='required'>*</span></Typography>
            <InputControl disabled={EntityData} placeholder="e.g: NL0001, BL0390" value={hfm} onChange={(e) => setHfm(e)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>Entity Description<span className='required'>*</span></Typography>
            <InputControl placeholder="e.g: Re:sources Belgium TS" value={entity} onChange={(e) => setEntity(e)} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>Agency<span className='required'>*</span></Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flexGrow: 1 }}>
                <DropDown placeholder="-- Select Agency --" options={agenciesList} onChange={changeAgency} value={agencyName} sx={{ flexGrow: 1 }} />
              </Box>
              <AddIcon onClick={() => setIsOpen(true)} sx={{
                color: "white",
                padding: "10px",
                background: " #212129",
                cursor: "pointer",
                marginLeft: "0px", // Adds spacing between the dropdown and the icon
              }} />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>Country<span className='required'>*</span></Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Box sx={{ flexGrow: 1 }}>
                <DropDown placeholder="-- Select Country --" value={countryName} options={countriesList} onChange={changeCountry} sx={{ flexGrow: 1 }} />
              </Box>
              <AddIcon onClick={() => setIsOpenCountry(true)} sx={{
                color: "white",
                padding: "10px",
                background: " #212129",
                cursor: "pointer",
                marginLeft: "0px", // Adds spacing between the dropdown and the icon
              }} />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>Solution Hub Name<span className='required'>*</span></Typography>
            <DropDown placeholder="-- Select Hub Name --" value={solutionHubName} onChange={changeHub} options={SolutionHubs} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>Solution Hub Short<span className='required'>*</span></Typography>
            <InputControl disabled value={solutionHubShort} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography sx={{ fontWeight: "bold" }}>Supervisor Email</Typography>
            <InputControl placeholder="Enter Supervisor Email" value={email} onChange={(e) => setEmail(e)} />
            {emailError && (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {emailError}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>

      <Grid container justifyContent="center" sx={{ margin: "20px" }}>
        <Button label={EntityData ? "Update" : "Save"} onClick={handleSave} isDisabled={!isFormValid()} />
        &nbsp;&nbsp;
        <Button label="Cancel" onClick={() => navigate(`/app-dcoi/sitecontent/ManageEntity`)} />
      </Grid>

      <Spinner isLoading={inProgress} />
    </>
  );
}

export default AddNewEntity;
